import React, { useState, useMemo } from 'react';
import s from './NativeTable.module.scss';

/**
 * NativeTable — single-table replacement for react-bootstrap-table.
 *
 * Props:
 *  data          {Array}    Row objects
 *  columns       {Array}    [{ key, header, render?(cell,row)=>node, hideMobile?, className? }]
 *  searchFields  {Array}    Field names to search across. Omit to hide search bar.
 *  onRowClick    {Function} Optional (row) => void for clickable rows
 *  getRowClassName {Function} Optional (row) => className string for <tr>
 *  getRowStyle   {Function} Optional (row) => style object for <tr>
 *  pageSize      {Number}   Rows per page (default 10)
 */
const NativeTable = ({ data = [], columns = [], searchFields, onRowClick, getRowClassName, getRowStyle, pageSize = 10, minWidth }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const showSearch = searchFields && searchFields.length > 0;
    const visibleCount = columns.filter(c => !c.hideMobile).length;
    const computedMinWidth = minWidth != null ? minWidth : (visibleCount > 5 ? visibleCount * 110 : undefined);

    const filtered = useMemo(() => {
        if (!showSearch || !searchTerm.trim()) return data;
        const term = searchTerm.toLowerCase();
        return data.filter(row =>
            searchFields.some(field => {
                const val = row[field];
                return val !== null && val !== undefined && String(val).toLowerCase().includes(term);
            })
        );
    }, [data, searchTerm, showSearch, searchFields]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const pageData = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    return (
        <div className={s.tableContainer}>
            {showSearch && (
                <div className={s.searchBar}>
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className={s.searchInput}
                    />
                </div>
            )}
            <div className={s.tableScroll}>
                <table className={s.table} style={{ minWidth: computedMinWidth }}>
                    <thead>
                        <tr>
                            {columns.map((col, i) => (
                                <th
                                    key={i}
                                    className={[col.hideMobile ? 'hide-mobile' : '', col.sticky ? s.stickyCol : '', col.className || ''].filter(Boolean).join(' ')}
                                >
                                    <span className="fs-sm">{col.header}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageData.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className={s.noData}>No data found.</td>
                            </tr>
                        ) : pageData.map((row, rowIdx) => (
                            <tr
                                key={rowIdx}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                className={[
                                    onRowClick ? s.clickableRow : '',
                                    typeof getRowClassName === 'function' ? getRowClassName(row) : '',
                                ].filter(Boolean).join(' ')}
                                style={typeof getRowStyle === 'function' ? getRowStyle(row) : undefined}
                            >
                                {columns.map((col, colIdx) => (
                                    <td
                                        key={colIdx}
                                        className={[col.hideMobile ? 'hide-mobile' : '', col.sticky ? s.stickyCol : '', col.className || ''].filter(Boolean).join(' ')}
                                    >
                                        {col.render
                                            ? col.render(row[col.key], row)
                                            : (row[col.key] !== null && row[col.key] !== undefined ? String(row[col.key]) : '')}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className={s.pagination}>
                <button
                    className={s.pageBtn}
                    disabled={safePage === 1}
                    onClick={() => setCurrentPage(safePage - 1)}
                >&#8249;</button>
                <span className={s.pageInfo}>Page {safePage} of {totalPages}</span>
                <button
                    className={s.pageBtn}
                    disabled={safePage === totalPages}
                    onClick={() => setCurrentPage(safePage + 1)}
                >&#8250;</button>
            </div>
        </div>
    );
};

export default NativeTable;
